import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { cuFormat } from "../coreUiTable/Format";
import { xlsxExport } from "../../utils/xlsxExport";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { ViewRawDataLinkIcon } from "../../../assets/images/svg";
import Table from "../coreUiTable/Table";
import { convertTimeToTimestamp, stripHTMLTagsFromText } from "../../utils/formatters";
import "./Table_styles.scss";

dayjs.extend(customParseFormat);

const TableWithCustomSearch = ({
  itemsPerPage,
  fileName,
  data = [],
  colHeaders = [],
  columns,
  rawDataLink,
  noDataMessage = "No data available",
  dateConversion = true,
  formatColumns = true,
  customFn,
  links = [],
  columnHeaderSlot,
  tableWrapperId,
  underTableSlot,
  onFilteredItemsChange,
  additionalDataToExport,
  overrideColumnCallback,
  hideFirstCol,
  onPageChange,
  showCount = true,
  stripHTMLTagsInColumns = [], //array of column indexes in which to remove tags.
  footerSlot
}) => {
  const [filterValue, setFilterValue] = useState("");
  const [filteredLinks, setFilteredLinks] = useState(links);

  const formattedSlots = formatColumns
    ? cuFormat({
        countTitle: "#",
        headers: colHeaders,
        columns,
        links: filteredLinks,
        customFn
      })
    : {};

  function convertDatesToTimestamps(arr, columnFormats) {
    return arr.map((row) => {
      return row.map((value, i) => {
        const format = columnFormats[i];
        if (format && format.type === "date" && format.dateFormat && value) {
          const parsed = dayjs(value, format.dateFormat, true);

          if (parsed.isValid()) {
            return parsed.valueOf();
          }

          return value;
        } else if (format && format.type === "time" && value) {
          return convertTimeToTimestamp(value);
        }
        return value;
      });
    });
  }

  const filteredTableData = useMemo(() => {
    const tempLinks = [];
    const filteredData = data.filter((row, idx) => {
      const isRowMatching = row.some((value) =>
        String(value).toLowerCase().includes(filterValue.toLowerCase())
      );
      if (isRowMatching) {
        tempLinks.push(links[idx]);
      }
      return isRowMatching;
    });

    setFilteredLinks(tempLinks);
    return filteredData;
  }, [filterValue]);

  let formattedData = dateConversion
    ? convertDatesToTimestamps(filteredTableData, columns)
    : filteredTableData;

  const getDataToExport = () => {
    const combinedData = additionalDataToExport
      ? [...filteredTableData, additionalDataToExport]
      : filteredTableData;

    if (stripHTMLTagsInColumns.length) {
      return combinedData.map((row) =>
        row.map((cell, index) =>
          stripHTMLTagsInColumns.includes(index) ? stripHTMLTagsFromText(cell, " ") : cell
        )
      );
    }

    return combinedData;
  };

  return (
    <div id="tableWithCustomSearch" className="customSearch-table">
      {data.length > 0 ? (
        <>
          <div className="col-sm-6 form-inline pl-0 c-datatable-filter search">
            <input
              className="form-control"
              type="text"
              placeholder="Search..."
              aria-label="table filter input"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            />
          </div>
          <div className="action-btn">
            {filteredTableData.length > 0 ? (
              <>
                <a
                  className="download-btn"
                  title="Export to Excel"
                  onClick={() => {
                    xlsxExport({
                      data: getDataToExport(),
                      headers: colHeaders,
                      fileName,
                      columns
                    });
                  }}
                >
                  <FontAwesomeIcon
                    style={{ color: "#8fc447", marginTop: "8px" }}
                    icon={faDownload}
                  />
                </a>
                {rawDataLink ? (
                  <a
                    style={{ marginTop: 8 }}
                    href={rawDataLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View Raw Data"
                  >
                    <ViewRawDataLinkIcon />
                  </a>
                ) : null}
              </>
            ) : (
              <>
                <a className="download-btn">
                  <FontAwesomeIcon
                    style={{ color: "#8fc447", marginTop: "8px" }}
                    icon={faDownload}
                  />
                </a>
                {rawDataLink ? (
                  <a
                    style={{ marginTop: 8 }}
                    href={rawDataLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View Raw Data"
                  >
                    <ViewRawDataLinkIcon />
                  </a>
                ) : null}
              </>
            )}
          </div>
          <Table
            tableFilter={false}
            table={{ colHeaders, data: formattedData }}
            sorter
            showCount={showCount}
            tableWrapperId={tableWrapperId}
            itemsPerPage={itemsPerPage}
            scopedSlots={formattedSlots}
            columnHeaderSlot={columnHeaderSlot}
            underTableSlot={underTableSlot}
            onFilteredItemsChange={onFilteredItemsChange}
            overrideColumnCallback={overrideColumnCallback}
            onPageChange={onPageChange}
            hideFirstCol={hideFirstCol}
            footerSlot={footerSlot}
          />
        </>
      ) : (
        <p className="error">{noDataMessage}</p>
      )}
    </div>
  );
};

export default TableWithCustomSearch;
